import axios from 'axios';

const getRuntimeApiBaseUrl = () => {
    const configured = String(process.env.REACT_APP_BACKEND_URL || '').trim();
    if (configured) return configured;

    if (typeof window !== 'undefined') {
        const host = String(window.location.hostname || '').toLowerCase();
        const isLocalHost =
            host === 'localhost' || host === '127.0.0.1' || host === '::1';
        if (!isLocalHost) {
            return `${window.location.origin}/api`;
        }
    }
    return 'http://localhost:5001/api';
};

const API_BASE_URL = getRuntimeApiBaseUrl();
// Scope tokens by API host so local vs production JWTs never collide.
const TOKEN_STORAGE_KEY = `cronpay_backend_tokens:${API_BASE_URL}`;
const AUTH_RETRY_FLAG = '__cpAuthRetried';

const normalizeWallet = (walletAddress) =>
    String(walletAddress || '').trim().toLowerCase();

const readTokenMap = () => {
    try {
        const rawValue = localStorage.getItem(TOKEN_STORAGE_KEY);
        return rawValue ? JSON.parse(rawValue) : {};
    } catch (error) {
        return {};
    }
};

const writeTokenMap = (tokenMap) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenMap));
};

export const getStoredBackendToken = (walletAddress) => {
    const tokenMap = readTokenMap();
    return tokenMap[normalizeWallet(walletAddress)] || '';
};

export const storeBackendToken = (walletAddress, token) => {
    const normalizedWallet = normalizeWallet(walletAddress);
    const tokenMap = readTokenMap();
    tokenMap[normalizedWallet] = token;
    writeTokenMap(tokenMap);
};

export const clearStoredBackendToken = (walletAddress) => {
    const tokenMap = readTokenMap();
    const key = normalizeWallet(walletAddress);
    if (Object.prototype.hasOwnProperty.call(tokenMap, key)) {
        delete tokenMap[key];
        writeTokenMap(tokenMap);
    }
};

/**
 * Best-effort decode of a JWT payload to detect expiry on the client. Returns
 * `true` whenever the token is missing, malformed, or past its `exp` claim
 * (with a small clock-skew buffer). We never *trust* the payload for auth –
 * only use it to decide when to refresh.
 */
const isJwtExpired = (token) => {
    if (!token) return true;
    try {
        const parts = String(token).split('.');
        if (parts.length !== 3) return true;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json =
            typeof atob === 'function'
                ? atob(base64)
                : Buffer.from(base64, 'base64').toString('utf8');
        const payload = JSON.parse(json);
        if (!payload?.exp) return false;
        // 30-second skew avoids edge cases where the token expires in flight.
        return Date.now() >= payload.exp * 1000 - 30_000;
    } catch (_error) {
        return true;
    }
};

const buildConfig = (token, options = {}) => ({
    ...options,
    headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
});

const extractData = (response) => response?.data?.data;

const extractTaskListResponse = (response) => ({
    items: response?.data?.data || [],
    pagination: response?.data?.pagination || {
        page: 1,
        limit: 15,
        total: 0,
        totalPages: 1,
    },
});

const buildTaskListParams = (params = {}) => {
    const query = {};
    if (params.fetchAll) query.fetchAll = 'true';
    if (params.page) query.page = String(params.page);
    if (params.limit) query.limit = String(params.limit);
    if (params.search) query.search = params.search;
    if (params.scope) query.scope = params.scope;
    if (params.statusFilter && params.statusFilter !== 'all') {
        query.statusFilter = params.statusFilter;
    }
    if (params.assignedToMe) query.assignedToMe = 'true';
    return query;
};

const isWalletSignatureRequired = () =>
    String(process.env.REACT_APP_REQUIRE_WALLET_SIGNATURE ?? 'true').toLowerCase() !==
    'false';

export const fetchWalletAuthNonce = async (walletAddress) => {
    const response = await axios.get(`${API_BASE_URL}/auth/nonce`, {
        params: { walletAddress },
    });
    return response.data.data;
};

export const registerEncryptionPublicKey = async (token, encryptionPublicKey) => {
    const response = await axios.put(
        `${API_BASE_URL}/auth/encryption-public-key`,
        { encryptionPublicKey },
        buildConfig(token),
    );
    return extractData(response);
};

export const getEncryptionPublicKey = async (token, walletAddress) => {
    const response = await axios.get(
        `${API_BASE_URL}/auth/encryption-public-key/${encodeURIComponent(walletAddress)}`,
        buildConfig(token),
    );
    const data = extractData(response);
    return data?.encryptionPublicKey || '';
};

/** Register / sign in with a wallet signature (creates user row in cronpayv2). */
export const connectWalletSessionWithSignature = async ({
    walletAddress,
    signMessageAsync,
    name,
    rolePreference,
}) => {
    if (!walletAddress) {
        throw new Error('walletAddress is required');
    }
    if (!signMessageAsync) {
        throw new Error('Wallet signature is required to sign in');
    }

    const noncePayload = await fetchWalletAuthNonce(walletAddress);
    const signature = await signMessageAsync({ message: noncePayload.message });

    const response = await axios.post(`${API_BASE_URL}/auth/connect-wallet`, {
        walletAddress,
        name,
        rolePreference,
        message: noncePayload.message,
        signature,
        nonce: noncePayload.nonce,
    });

    return response.data.data;
};

export const connectWalletSession = async ({
    walletAddress,
    name,
    rolePreference,
    signMessageAsync,
}) => {
    if (isWalletSignatureRequired() || signMessageAsync) {
        if (!signMessageAsync) {
            throw new Error('Wallet signature is required to sign in');
        }
        return connectWalletSessionWithSignature({
            walletAddress,
            signMessageAsync,
            name,
            rolePreference,
        });
    }

    const response = await axios.post(`${API_BASE_URL}/auth/connect-wallet`, {
        walletAddress,
        name,
        rolePreference,
    });

    return response.data.data;
};

/**
 * Returns a backend JWT for the given wallet. When signature auth is enabled,
 * pass `signMessageAsync` from wagmi to register/sign in; otherwise only a
 * cached non-expired token is returned.
 */
export const ensureBackendSession = async (
    walletAddress,
    { forceRefresh = false, signMessageAsync, rolePreference } = {},
) => {
    if (!walletAddress) return null;

    const existing = getStoredBackendToken(walletAddress);
    if (!forceRefresh && existing && !isJwtExpired(existing)) {
        return existing;
    }

    if (existing) {
        clearStoredBackendToken(walletAddress);
    }

    if (!signMessageAsync && isWalletSignatureRequired()) {
        return null;
    }

    try {
        const data = await connectWalletSession({
            walletAddress,
            signMessageAsync,
            rolePreference,
        });
        const token = data?.token;
        if (token) {
            storeBackendToken(walletAddress, token);
            return token;
        }
    } catch (error) {
        if (typeof console !== 'undefined') {
            console.warn(
                'ensureBackendSession failed:',
                error?.response?.data?.message || error?.message,
            );
        }
    }
    return null;
};

/**
 * Mint or refresh a backend JWT for dispute moderation pages.
 * Admin privilege is granted only by server allowlist / on-chain authority.
 */
export const ensureAdminBackendSession = async (
    walletAddress,
    { signMessageAsync, forceRefresh = false } = {},
) => {
    if (!walletAddress) return null;

    const existing = getStoredBackendToken(walletAddress);
    if (!forceRefresh && existing && !isJwtExpired(existing)) {
        return existing;
    }

    if (existing) {
        clearStoredBackendToken(walletAddress);
    }

    if (!signMessageAsync && isWalletSignatureRequired()) {
        return null;
    }

    try {
        const data = await connectWalletSession({
            walletAddress,
            rolePreference: 'employee',
            signMessageAsync,
        });
        const token = data?.token;
        if (token) {
            storeBackendToken(walletAddress, token);
            return token;
        }
    } catch (error) {
        if (typeof console !== 'undefined') {
            console.warn(
                'ensureAdminBackendSession failed:',
                error?.response?.data?.message || error?.message,
            );
        }
    }

    if (signMessageAsync) {
        return ensureBackendSession(walletAddress, {
            forceRefresh: true,
            signMessageAsync,
            rolePreference: 'employee',
        });
    }

    return null;
};

const extractWalletFromAuthHeader = (headerValue) => {
    if (!headerValue) return null;
    const token = String(headerValue).replace(/^Bearer\s+/i, '').trim();
    if (!token) return null;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json =
            typeof atob === 'function'
                ? atob(base64)
                : Buffer.from(base64, 'base64').toString('utf8');
        return JSON.parse(json)?.walletAddress || null;
    } catch (_error) {
        return null;
    }
};

/** 401 responses where a fresh /auth/connect-wallet token should fix the request. */
const isStaleAuthFailure = (response) => {
    if (!response || response.status !== 401) return false;
    const message = String(response?.data?.message || '').toLowerCase();
    return (
        message.includes('user not found') ||
        message.includes('jwt expired') ||
        message.includes('jwt malformed') ||
        message.includes('invalid signature') ||
        message.includes('invalid token') ||
        message.includes('token is invalid') ||
        message.includes('authentication') ||
        message.includes('wallet signature is required') ||
        message.includes('sign-in nonce') ||
        message.includes('invalid wallet signature') ||
        message.includes('signature does not match') ||
        message.includes('sign-in message mismatch')
    );
};

// Global self-healing for expired JWTs: when a request to our API returns 401
// because of an expired token, mint a fresh one for the same wallet and retry
// the original request once. Other 401 sources fall through unchanged.
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error?.config;
        const response = error?.response;

        if (
            !originalRequest ||
            !response ||
            originalRequest[AUTH_RETRY_FLAG] ||
            !String(originalRequest?.url || '').startsWith(API_BASE_URL) ||
            !isStaleAuthFailure(response)
        ) {
            return Promise.reject(error);
        }

        const wallet = extractWalletFromAuthHeader(
            originalRequest?.headers?.Authorization ||
                originalRequest?.headers?.authorization,
        );
        if (!wallet) {
            return Promise.reject(error);
        }

        clearStoredBackendToken(wallet);
        originalRequest[AUTH_RETRY_FLAG] = true;

        if (isWalletSignatureRequired()) {
            return Promise.reject(error);
        }

        const freshToken = await ensureBackendSession(wallet, { forceRefresh: true });
        if (!freshToken) {
            return Promise.reject(error);
        }

        originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${freshToken}`,
        };
        return axios(originalRequest);
    },
);

export const getMyCompany = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/company/me`,
        buildConfig(token),
    );
    return extractData(response);
};

export const getMyEmployeeCompany = async (token) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/company/employee/me`,
            buildConfig(token),
        );
        return extractData(response);
    } catch (error) {
        if (error?.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

export const getMyEmployeeProfile = async (token) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/company/employee/profile`,
            buildConfig(token),
        );
        return extractData(response);
    } catch (error) {
        if (error?.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

export const updateMyEmployeeProfile = async (token, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/company/employee/profile`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const createCompany = async (token, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/company/create`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const updateCompany = async (token, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/company/me`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const updateCompanyOnChainRegistry = async (token, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/company/me/on-chain-registry`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const updateEmployeeOnChainRegistry = async (token, employeeId, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/company/employees/${employeeId}/on-chain-registry`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const listEmployees = async (token, params = {}) => {
    const query = {};
    if (params.search) {
        query.search = params.search;
    }
    const response = await axios.get(`${API_BASE_URL}/company/employees`, {
        ...buildConfig(token),
        params: query,
    });
    return extractData(response) || [];
};

export const addEmployee = async (token, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/company/employees/add`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const updateEmployee = async (token, employeeId, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/company/employees/${employeeId}`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const removeEmployee = async (token, employeeId) => {
    const response = await axios.delete(
        `${API_BASE_URL}/company/employees/${employeeId}`,
        buildConfig(token),
    );
    return response.data;
};

export const createTask = async (token, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/create`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const getCompanyTasks = async (token, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/tasks/company`, {
        ...buildConfig(token),
        params: buildTaskListParams(params),
    });
    if (params.fetchAll || (!params.page && !params.limit)) {
        return extractTaskListResponse(response).items;
    }
    return extractTaskListResponse(response);
};

export const getTaskById = async (token, taskId) => {
    const response = await axios.get(
        `${API_BASE_URL}/tasks/${taskId}`,
        buildConfig(token),
    );
    return extractData(response);
};

export const getTaskOnChainState = async (token, taskId) => {
    const response = await axios.get(
        `${API_BASE_URL}/tasks/${taskId}/onchain-state`,
        buildConfig(token),
    );
    return extractData(response);
};

export const getEmployeeTasks = async (token, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/tasks/employee`, {
        ...buildConfig(token),
        params: buildTaskListParams(params),
    });
    if (params.fetchAll || (!params.page && !params.limit)) {
        return extractTaskListResponse(response).items;
    }
    return extractTaskListResponse(response);
};

export const acceptTask = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/accept`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const submitTask = async (token, taskId, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/submit`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

/** Multipart upload; `API_BASE_URL` must already include `/api` (no extra `/api` prefix). */
export const uploadTaskFile = async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
        `${API_BASE_URL}/tasks/upload`,
        formData,
        buildConfig(token),
    );
    return extractData(response);
};

export const approveTask = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/approve`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const completeTask = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/complete`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const rejectTask = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/reject`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const claimTaskPayment = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/claim-payment`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const deleteTask = async (token, taskId) => {
    const response = await axios.delete(
        `${API_BASE_URL}/tasks/${taskId}`,
        buildConfig(token),
    );
    return extractData(response);
};

export const raiseDispute = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/dispute`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const getTaskDispute = async (token, taskId) => {
    const response = await axios.get(
        `${API_BASE_URL}/tasks/${taskId}/dispute`,
        buildConfig(token),
    );
    return extractData(response);
};

export const resolveTaskDispute = async (token, taskId, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/dispute/resolve`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const requestRefund = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/refund-request`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const acceptRefund = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/refund-accept`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const rejectRefund = async (token, taskId, payload = {}) => {
    const response = await axios.post(
        `${API_BASE_URL}/tasks/${taskId}/refund-reject`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const recordEscrowCreated = async (token, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/payments/escrow-created`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const markPaymentRefunded = async (token, taskId, payload = {}) => {
    const response = await axios.patch(
        `${API_BASE_URL}/payments/${taskId}/refund`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const recordDirectPayment = async (token, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/direct-payments`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

/**
 * Save an on-chain payment row after tx confirmation. Retries auth once and
 * returns `{ ok: true }` or `{ ok: false, reason }` so callers can notify the user.
 * Pass `signMessageAsync` so an expired JWT can be refreshed after the on-chain tx.
 */
export const persistPaymentRecord = async (
    walletAddress,
    payload,
    { signMessageAsync } = {},
) => {
    if (!walletAddress) {
        return { ok: false, reason: 'Wallet not connected' };
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
        const forceRefresh = attempt > 0;
        if (forceRefresh) {
            clearStoredBackendToken(walletAddress);
        }

        const token = await ensureBackendSession(walletAddress, {
            forceRefresh,
            signMessageAsync,
        });
        if (!token) {
            return {
                ok: false,
                reason: signMessageAsync
                    ? 'Could not sign in to the API. Approve the wallet signature request, then try again.'
                    : 'Backend session expired. Sign in with your wallet, then retry saving this payment.',
            };
        }

        try {
            await recordDirectPayment(token, payload);
            return { ok: true };
        } catch (error) {
            const status = error?.response?.status;
            const staleAuth =
                isStaleAuthFailure(error?.response) ||
                status === 401;
            if (staleAuth && attempt === 0 && signMessageAsync) {
                continue;
            }
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save payment to the server';
            return { ok: false, reason: message };
        }
    }

    return { ok: false, reason: 'Failed to save payment to the server' };
};

export const listMyDirectPayments = async (token, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/direct-payments/my`, {
        ...buildConfig(token),
        params,
    });
    const records = extractData(response) || [];
    if (params.page !== undefined && params.page !== null && params.page !== '') {
        return {
            records,
            pagination: response?.data?.pagination || {
                page: 1,
                limit: records.length,
                total: records.length,
                totalPages: 1,
            },
        };
    }
    return records;
};

export const updateDirectPayment = async (token, recordId, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/direct-payments/${recordId}`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const recordPaymentRequest = async (token, payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-requests`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

export const listMyPaymentRequests = async (token, params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/payment-requests/my`, {
        ...buildConfig(token),
        params,
    });
    const records = extractData(response) || [];
    if (params.page !== undefined && params.page !== null && params.page !== '') {
        return {
            records,
            pagination: response?.data?.pagination || {
                page: 1,
                limit: records.length,
                total: records.length,
                totalPages: 1,
            },
        };
    }
    return records;
};

export const updatePaymentRequestByRequestId = async (token, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/payment-requests/by-request-id`,
        payload,
        buildConfig(token),
    );
    return extractData(response);
};

/** Save a Get Paid request row after on-chain request tx confirmation. */
export const persistPaymentRequestRecord = async (
    walletAddress,
    payload,
    { signMessageAsync } = {},
) => {
    if (!walletAddress) {
        return { ok: false, reason: 'Wallet not connected' };
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
        const forceRefresh = attempt > 0;
        if (forceRefresh) {
            clearStoredBackendToken(walletAddress);
        }

        const token = await ensureBackendSession(walletAddress, {
            forceRefresh,
            signMessageAsync,
        });
        if (!token) {
            return {
                ok: false,
                reason: signMessageAsync
                    ? 'Could not sign in to the API. Approve the wallet signature request, then try again.'
                    : 'Backend session expired. Sign in with your wallet, then retry saving this request.',
            };
        }

        try {
            await recordPaymentRequest(token, payload);
            return { ok: true };
        } catch (error) {
            const status = error?.response?.status;
            const staleAuth =
                isStaleAuthFailure(error?.response) || status === 401;
            if (staleAuth && attempt === 0 && signMessageAsync) {
                continue;
            }
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save payment request to the server';
            return { ok: false, reason: message };
        }
    }

    return { ok: false, reason: 'Failed to save payment request to the server' };
};

/** Update request status when payer accepts (or rejects on-chain later). */
export const persistPaymentRequestResolution = async (
    walletAddress,
    payload,
    { signMessageAsync } = {},
) => {
    if (!walletAddress) {
        return { ok: false, reason: 'Wallet not connected' };
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
        const forceRefresh = attempt > 0;
        if (forceRefresh) {
            clearStoredBackendToken(walletAddress);
        }

        const token = await ensureBackendSession(walletAddress, {
            forceRefresh,
            signMessageAsync,
        });
        if (!token) {
            return {
                ok: false,
                reason: signMessageAsync
                    ? 'Could not sign in to the API. Approve the wallet signature request, then try again.'
                    : 'Backend session expired. Sign in with your wallet, then retry.',
            };
        }

        try {
            await updatePaymentRequestByRequestId(token, payload);
            return { ok: true };
        } catch (error) {
            const staleAuth = isStaleAuthFailure(error?.response);
            if (staleAuth && attempt === 0) {
                continue;
            }
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to update payment request on the server';
            return { ok: false, reason: message };
        }
    }

    return { ok: false, reason: 'Failed to update payment request on the server' };
};

export const getNotifications = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/notifications`,
        buildConfig(token),
    );
    return extractData(response) || [];
};

export const markNotificationRead = async (token, notificationId) => {
    const response = await axios.patch(
        `${API_BASE_URL}/notifications/${notificationId}/read`,
        {},
        buildConfig(token),
    );
    return extractData(response);
};

/** Payment disputes involving the signed-in wallet (raiser, sender, or receiver) */
export const getMyPaymentDisputes = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/disputes/my/payment-disputes`,
        buildConfig(token),
    );
    return extractData(response) || [];
};

export const getMyDisputes = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/disputes/my`,
        buildConfig(token),
    );
    return extractData(response) || [];
};

export const getDisputeStats = async (token) => {
    const response = await axios.get(
        `${API_BASE_URL}/disputes/stats`,
        buildConfig(token),
    );
    return extractData(response);
};

export const getAdminDisputeById = async (token, disputeId) => {
    const response = await axios.get(
        `${API_BASE_URL}/disputes/${disputeId}`,
        buildConfig(token),
    );
    return extractData(response);
};

export const updateDisputeStatus = async (token, disputeId, status) => {
    const response = await axios.patch(
        `${API_BASE_URL}/disputes/${disputeId}/status`,
        { status },
        buildConfig(token),
    );
    return extractData(response);
};

/** Dispute discussion (admin + parties); requires JWT */
export const getDisputeMessages = async (token, disputeId) => {
    const response = await axios.get(
        `${API_BASE_URL}/disputes/${disputeId}/messages`,
        buildConfig(token),
    );
    const data = extractData(response);
    if (Array.isArray(data)) {
        return { messages: data, unreadCount: 0, disputeStatus: null };
    }
    return {
        messages: data?.messages || [],
        unreadCount: data?.unreadCount ?? 0,
        lastReadAt: data?.lastReadAt,
        disputeStatus: data?.disputeStatus,
    };
};

export const postDisputeMessage = async (token, disputeId, body, attachment = null) => {
    const form = new FormData();
    form.append('body', body || '');
    if (attachment) form.append('attachment', attachment);
    const response = await axios.post(
        `${API_BASE_URL}/disputes/${disputeId}/messages`,
        form,
        {
            ...buildConfig(token),
            headers: {
                ...buildConfig(token).headers,
                'Content-Type': 'multipart/form-data',
            },
        },
    );
    return extractData(response);
};

export const markDisputeRead = async (token, disputeId) => {
    const response = await axios.post(
        `${API_BASE_URL}/disputes/${disputeId}/read`,
        {},
        buildConfig(token),
    );
    return extractData(response);
};

export const backendApi = {
    addEmployee,
    acceptRefund,
    acceptTask,
    approveTask,
    claimTaskPayment,
    completeTask,
    connectWalletSession,
    createCompany,
    createTask,
    clearStoredBackendToken,
    deleteTask,
    ensureBackendSession,
    ensureAdminBackendSession,
    getEncryptionPublicKey,
    getCompanyTasks,
    getEmployeeTasks,
    getTaskById,
    getTaskOnChainState,
    getMyCompany,
    getMyEmployeeCompany,
    getMyEmployeeProfile,
    updateMyEmployeeProfile,
    getNotifications,
    markNotificationRead,
    getMyPaymentDisputes,
    getMyDisputes,
    getDisputeStats,
    getAdminDisputeById,
    updateDisputeStatus,
    getTaskDispute,
    resolveTaskDispute,
    getDisputeMessages,
    postDisputeMessage,
    markDisputeRead,
    listEmployees,
    listMyDirectPayments,
    raiseDispute,
    getTaskDispute,
    resolveTaskDispute,
    recordEscrowCreated,
    markPaymentRefunded,
    recordDirectPayment,
    recordPaymentRequest,
    listMyPaymentRequests,
    updatePaymentRequestByRequestId,
    persistPaymentRecord,
    persistPaymentRequestRecord,
    persistPaymentRequestResolution,
    registerEncryptionPublicKey,
    rejectRefund,
    rejectTask,
    removeEmployee,
    requestRefund,
    submitTask,
    uploadTaskFile,
    updateCompany,
    updateCompanyOnChainRegistry,
    updateEmployeeOnChainRegistry,
    updateDirectPayment,
    updateEmployee,
};

export { API_BASE_URL };
